import { formatCalendarDate } from "@/lib/date";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import PurchaseOrderForm from "@/features/inventory/PurchaseOrderForm";
import { DataTable, type DataTableColumnDef } from "@/components/ui/data-table";
import OrderDetailsDrawer from "@/features/inventory/OrderDetailsDrawer";
import { screamingSnakeCaseToTitle } from "@/lib/utils";
import { Edit, InboxIcon, Trash2 } from "lucide-react";

export const Route = createFileRoute("/inventory/orders")({
  component: OrdersPage,
});

type Order = RouterOutputs["inventory"]["purchaseOrder"]["list"][number];

function OrdersPage() {
  const navigate = Route.useNavigate();
  const { data: orders = [] } = trpc.inventory.purchaseOrder.list.useQuery();

  const utils = trpc.useUtils();
  const deletePurchaseOrder = trpc.inventory.purchaseOrder.delete.useMutation({
    onSuccess: () => {
      utils.inventory.purchaseOrder.list.invalidate();
    },
  });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState<number | null>(null);

  const columns = useMemo(() => {
    const cols: DataTableColumnDef<Order, any>[] = [
      { id: "id", header: "PO #", accessorKey: "id", cellTypeId: "string" },
      {
        id: "supplier_name",
        header: "Supplier",
        accessorKey: "supplier_name",
        cellTypeId: "string",
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => screamingSnakeCaseToTitle(row.original.status),
      },
      {
        id: "order_date",
        header: "Order Date",
        accessorKey: "order_date",
        cell: ({ row }) =>
          row.original.order_date
            ? formatCalendarDate(row.original.order_date)
            : "-",
      },
      {
        id: "expected_delivery_date",
        header: "Expected",
        accessorKey: "expected_delivery_date",
        cell: ({ row }) =>
          row.original.expected_delivery_date
            ? formatCalendarDate(row.original.expected_delivery_date)
            : "-",
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setActiveOrderId(row.original.id as number);
                setDrawerOpen(true);
              }}
              title="Edit Order"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                navigate({
                  to: "/inventory/receiving",
                  search: { po: row.original.id },
                });
              }}
              title="Start Receiving"
            >
              <InboxIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (
                  window.confirm("Are you sure you want to delete this order?")
                ) {
                  deletePurchaseOrder.mutate(row.original.id);
                }
              }}
              title="Delete Order"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
        enableSorting: false,
      },
    ];
    return cols;
  }, []);

  return (
    <div className="gap-4 flex flex-col grow min-h-0 h-full">
      <DataTable
        data={orders}
        columns={columns}
        searchPlaceholder="Search purchase orders..."
        leftCustomToolbar={
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm">Create Order</Button>
            </DialogTrigger>
            <DialogContent className="max-w-[min(90vw,920px)]">
              <PurchaseOrderForm
                asModal
                onComplete={() => setIsCreateOpen(false)}
              />
            </DialogContent>
          </Dialog>
        }
        enableColumnResizing
        maxHeight="none"
        className="grow min-h-0 flex-1"
      />

      <OrderDetailsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        orderId={activeOrderId}
      />
    </div>
  );
}
